import client from '@/services/http/client'
import { isMockEnabled, mockDelay } from '@/services/http/mock'
import type { DashboardSummary, PilotDashboard, JobRunItem } from '@/types/dashboard'
import type { PaginatedData } from '@/types/api'

const mockSummary: DashboardSummary = {
  today_new_message_count: 12,
  pending_response_count: 5,
  pending_draft_confirm_count: 3,
  waiting_customer_confirm_count: 4,
  open_sla_event_count: 2,
  binding_required_count: 1,
  last_sla_scan_at: new Date().toISOString(),
  recent_job_failure_count: 2,
  unsynced_task_count: 0,
  naming_anomaly_count: 1,
}

const mockPilot: PilotDashboard = {
  period: { from: new Date(Date.now() - 7 * 86400000).toISOString(), to: new Date().toISOString() },
  summary: {
    total_messages: 86,
    total_drafts: 42,
    draft_conversion_rate: 0.5714,
    total_tickets_created: 24,
    ticket_creation_success_rate: 0.5714,
    total_sla_events: 12,
    sla_breach_count: 3,
    sync_failures: 2,
    notification_failures: 1,
    knowledge_entries: 15,
  },
  recent_failed_jobs: [
    { run_id: 'mock-1', job_code: 'task_sync_incremental', error_message: 'Connection timeout', started_at: new Date().toISOString() },
  ],
  generated_at: new Date().toISOString(),
}

export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  if (isMockEnabled()) {
    await mockDelay()
    return { ...mockSummary }
  }
  const { data } = await client.get<DashboardSummary>('/dashboard/summary')
  return data
}

export async function fetchPilotDashboard(days = 7): Promise<PilotDashboard> {
  if (isMockEnabled()) {
    await mockDelay()
    return { ...mockPilot }
  }
  const { data } = await client.get<PilotDashboard>('/system/pilot-dashboard', { params: { days } })
  return data
}

// ── Job Runs ──

const mockJobRuns: JobRunItem[] = [
  {
    id: 1,
    run_id: 'sla_scan_open_chain-a1b2c3d4e5',
    job_code: 'sla_scan_open_chain',
    status: 'success',
    started_at: new Date(Date.now() - 3600000).toISOString(),
    finished_at: new Date(Date.now() - 3500000).toISOString(),
    duration_ms: 100000,
    candidate_count: 15,
    success_count: 12,
    failure_count: 0,
    warning_count: 3,
    result_summary: 'detected=15, created=3, updated=9',
    error_message: null,
  },
  {
    id: 2,
    run_id: 'task_sync_incremental-f6g7h8i9j0',
    job_code: 'task_sync_incremental',
    status: 'failure',
    started_at: new Date(Date.now() - 1800000).toISOString(),
    finished_at: new Date(Date.now() - 1790000).toISOString(),
    duration_ms: 10000,
    candidate_count: 8,
    success_count: 0,
    failure_count: 8,
    warning_count: 0,
    result_summary: null,
    error_message: 'Connection timeout',
  },
  {
    id: 3,
    run_id: 'sla_notify_dispatch-k1l2m3n4o5',
    job_code: 'sla_notify_dispatch',
    status: 'partial',
    started_at: new Date(Date.now() - 900000).toISOString(),
    finished_at: new Date(Date.now() - 850000).toISOString(),
    duration_ms: 50000,
    candidate_count: 10,
    success_count: 7,
    failure_count: 2,
    warning_count: 1,
    result_summary: 'candidates=10, dispatched=9, skipped=1, sent=7, failed=2',
    error_message: '2 notifications failed delivery',
  },
]

export async function fetchJobRuns(params: {
  page?: number
  page_size?: number
  job_code?: string
}): Promise<PaginatedData<JobRunItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    let filtered = [...mockJobRuns]
    if (params.job_code) {
      filtered = filtered.filter((j) => j.job_code === params.job_code)
    }
    const page = params.page || 1
    const page_size = params.page_size || 20
    const start = (page - 1) * page_size
    return {
      page,
      page_size,
      total: filtered.length,
      items: filtered.slice(start, start + page_size),
    }
  }
  const { data } = await client.get<PaginatedData<JobRunItem>>('/system/job-runs', { params })
  return data
}
